import {useRedirector} from '@tnc/shared';
import {useState} from 'react';
import {Link, useNavigate} from 'react-router';
import SimpleBar from 'simplebar-react';

import {usdFormatter} from '@/lib/helpers';

import {useApp} from '@/providers/components/appProvider';
import {useAuth} from '@/providers/components/authProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

import Modalsearch from '@/components/common/modalsearch/modalsearch';
import Icon from '@/components/icon';

function Header() {
  const navigate = useNavigate();
  const {toggleDarkMode, toggleSidebar} = useApp();
  const {
    handleSignOut,
    userAccounts: {speculator},
    accountBalance,
    heldBalance,
  } = useAuth();
  const {createReferralLink} = useRedirector();
  const {notifications, unreadCount, markAsRead, markAllAsRead, archiveNotification} = useNotifications();

  // State
  const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);
  const [referralLink, setReferralLink] = useState('');
  const [isCopied, setIsCopied] = useState(false);

  // Functions
  const handleNotificationClick = async (notification: {notification_id: string; link?: string | null}) => {
    await markAsRead(notification.notification_id);
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const handleNotificationClose = async (e: React.MouseEvent<HTMLAnchorElement>, notificationId: string) => {
    e.stopPropagation();
    await archiveNotification(notificationId);
  };

  const ToggleDark = () => {
    toggleDarkMode();
  };

  const handleSidebarToggle = () => {
    toggleSidebar();
  };

  /**
   * Logs the user out and redirects to '/'
   */
  const handleLogOut = async () => {
    await handleSignOut();
    navigate('/');
  };

  /**
   * Generates a referral link for the speculator
   */
  const generateReferralLink = async () => {
    try {
      const link = await createReferralLink({
        name: `${speculator?.speculator_username || 'Anonymous'}'s Exchange Campaign`,
        application_type: 'exchange',
        referral_code: `EX${Math.random().toString(36).substring(2, 8).toUpperCase()}`,
      });

      setReferralLink(link);
      setIsReferralModalOpen(true);
    } catch (error) {
      console.error('Error generating referral link:', error);
    }
  };

  /**
   * Copies the referral link to the clipboard
   */
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
  };

  return (
    <>
      <header className="app-header">
        <nav className="main-header !h-[3.75rem]" aria-label="Global">
          <div className="main-header-container pe-[1rem] ps-[0.725rem]">
            <div className="header-content-left">
              <div className="header-element">
                <div className="horizontal-logo">
                  <a href={`/`} className="header-logo">
                    <img src={'/images/logo/logo.png'} alt="logo" className="desktop-logo" />
                    <img src={'/images/logo/logo.png'} alt="logo" className="toggle-logo" />
                    <img src={'/images/logo/logo.png'} alt="logo" className="desktop-dark" />
                    <img src={'/images/logo/logo.png'} alt="logo" className="toggle-dark" />
                    <img src={'/images/logo/logo.png'} alt="logo" className="desktop-white" />
                    <img src={'/images/logo/logo.png'} alt="logo" className="toggle-white" />
                  </a>
                </div>
              </div>
              <div className="header-element !items-center md:px-[0.325rem]" onClick={handleSidebarToggle}>
                <Link
                  aria-label="Hide Sidebar"
                  className="sidemenu-toggle animated-arrow hor-toggle horizontal-navtoggle inline-flex items-center"
                  to="#"
                >
                  <span></span>
                </Link>
              </div>
            </div>

            <div className="header-content-right">
              {/* <div className="header-element header-search px-2 py-[1rem] md:px-[0.65rem]">
                <button
                  aria-label="button"
                  type="button"
                  data-hs-overlay="#search-modal"
                  className="dark:bg-bgdark inline-flex flex-shrink-0 items-center justify-center gap-2 rounded-full text-xs font-medium transition-all focus:ring-offset-0 focus:ring-offset-white dark:text-[#8c9097] dark:text-white/50 dark:hover:bg-black/20 dark:hover:text-white dark:focus:ring-white/10 dark:focus:ring-offset-white/10"
                >
                  <i className="bx bx-search-alt-2 header-link-icon"></i>
                </button>
              </div> */}
              <div
                className="header-element header-theme-mode hidden !items-center !py-[1rem] px-2 sm:block md:!px-[0.65rem]"
                onClick={() => ToggleDark()}
              >
                <Link
                  aria-label="anchor"
                  className="hs-dark-mode dark:bg-bgdark group flex flex-shrink-0 items-center justify-center gap-2 rounded-full text-xs font-medium transition-all hs-dark-mode-active:hidden dark:text-[#8c9097] dark:text-white/50 dark:hover:bg-black/20 dark:hover:text-white dark:focus:ring-white/10 dark:focus:ring-offset-white/10"
                  to="#"
                  data-hs-theme-click-value="dark"
                >
                  <i className="bx bx-moon header-link-icon"></i>
                </Link>
                <Link
                  aria-label="anchor"
                  className="hs-dark-mode dark:bg-bgdark group hidden flex-shrink-0 items-center justify-center gap-2 rounded-full text-xs font-medium text-defaulttextcolor transition-all hs-dark-mode-active:flex dark:bg-bodybg dark:text-[#8c9097] dark:text-white/50 dark:hover:bg-black/20 dark:hover:text-white dark:focus:ring-white/10 dark:focus:ring-offset-white/10"
                  to="#"
                  data-hs-theme-click-value="light"
                >
                  <i className="bx bx-sun header-link-icon"></i>
                </Link>
              </div>
              <div className="header-element notifications-dropdown header-notification hs-dropdown ti-dropdown !hidden px-2 py-[1rem] [--placement:bottom-right] md:!block md:px-[0.65rem] rtl:[--placement:bottom-left]">
                <button
                  id="dropdown-notification"
                  type="button"
                  className="hs-dropdown-toggle ti-dropdown-toggle relative flex-shrink-0 !rounded-full !border-0 !p-0 align-middle text-xs !shadow-none"
                >
                  <i className="bx bx-bell header-link-icon text-[1.125rem]"></i>
                  {unreadCount > 0 && (
                    <span className="absolute -top-[0.25rem] end-0 -me-[0.6rem] flex h-5 w-5">
                      <span className="absolute -start-[2px] -top-[2px] inline-flex h-full w-full animate-slow-ping rounded-full bg-secondary/40 opacity-75"></span>
                      <span
                        className="relative inline-flex h-[14.7px] w-[14px] items-center justify-center rounded-full bg-secondary text-[0.625rem] text-white"
                        id="notification-icon-badge"
                      >
                        {unreadCount}
                      </span>
                    </span>
                  )}
                </button>
                <div
                  className="main-header-dropdown hs-dropdown-menu ti-dropdown-menu !m-0 !-mt-3 hidden !w-[22rem] border-0 border-defaultborder bg-white !p-0"
                  aria-labelledby="dropdown-notification"
                >
                  <div className="ti-dropdown-header !m-0 flex items-center justify-between !bg-transparent !p-4">
                    <p className="mb-0 text-[1.0625rem] font-semibold text-defaulttextcolor dark:text-[#8c9097] dark:text-white/50">
                      Notifications
                    </p>
                    {unreadCount > 0 && (
                      <span
                        className="rounded-sm bg-secondary/10 px-[0.45rem] py-[0.25rem/2] text-[0.75em] font-[600] text-secondary"
                        id="notification-data"
                      >
                        {`${unreadCount} Unread`}
                      </span>
                    )}
                  </div>
                  <div className="dropdown-divider"></div>
                  <ul className="end-0 !m-0 list-none !p-0">
                    <SimpleBar id="header-notification-scroll">
                      {notifications.map(notification => (
                        <li
                          className="ti-dropdown-item dropdown-item cursor-pointer"
                          key={notification.notification_id}
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <div className="flex items-start">
                            <div className="pe-2">
                              <span
                                className={`inline-flex text-${notification.type} !h-[2.5rem] !w-[2.5rem] items-center justify-center !text-[0.8rem] !leading-[2.5rem] ${notification.type}/10 !rounded-[50%]`}
                              >
                                <i
                                  className={`ti ti-${
                                    notification.type === 'info'
                                      ? 'info-circle'
                                      : notification.type === 'success'
                                        ? 'check-circle'
                                        : notification.type === 'warning'
                                          ? 'alert-triangle'
                                          : 'alert-circle'
                                  } text-[1.125rem]`}
                                ></i>
                              </span>
                            </div>
                            <div className="flex grow items-center justify-between">
                              <div>
                                <p className="mb-0 text-[0.8125rem] font-semibold text-defaulttextcolor dark:text-[#8c9097] dark:text-white/50">
                                  {notification.title}
                                </p>
                                <span className="header-notification-text text-[0.75rem] font-normal text-[#8c9097] dark:text-white/50">
                                  {notification.message}
                                </span>
                              </div>
                              <div>
                                <Link
                                  aria-label="anchor"
                                  to="#"
                                  onClick={e => handleNotificationClose(e, notification.notification_id)}
                                  className="dropdown-item-close1 me-1 min-w-fit text-[#8c9097] dark:text-white/50"
                                >
                                  <i className="ti ti-x text-[1rem]"></i>
                                </Link>
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </SimpleBar>
                  </ul>

                  <div className={`empty-header-item1 mt-2 border-t p-4 ${notifications.length === 0 ? 'hidden' : ''}`}>
                    <div className="grid gap-2">
                      <button onClick={markAllAsRead} className="ti-btn ti-btn-primary-full !m-0 w-full p-2">
                        Mark All as Read
                      </button>
                      <Link to="/speculator/notifications" className="ti-btn ti-btn-secondary-full !m-0 w-full p-2">
                        View All Notifications
                      </Link>
                    </div>
                  </div>
                  <div className={`empty-item1 p-[3rem] ${notifications.length === 0 ? '' : 'hidden'}`}>
                    <div className="text-center">
                      <span className="avatar !h-[4rem] !w-[4rem] !rounded-full !bg-secondary/10 !leading-[4rem] !text-secondary">
                        <i className="ri-notification-off-line text-[2rem]"></i>
                      </span>
                      <h6 className="mt-3 text-[1rem] font-semibold text-defaulttextcolor dark:text-[#8c9097] dark:text-white/50">
                        No New Notifications
                      </h6>
                    </div>
                  </div>
                </div>
              </div>

              <div className="header-element balance-icon px-2 py-[1rem] md:px-[0.65rem]">
                <button
                  type="button"
                  className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-white"
                  aria-label="Account Balance"
                >
                  <i className="ti ti-wallet text-[1.125rem]"></i>
                  <span className="text-success">{usdFormatter.format(accountBalance)}</span>
                </button>
              </div>

              <div className="header-element hs-dropdown ti-dropdown !items-center px-2 [--placement:bottom-left] md:!px-[0.65rem]">
                <button
                  id="dropdown-profile"
                  type="button"
                  className="hs-dropdown-toggle ti-dropdown-toggle me-0 flex-shrink-0 !gap-2 !rounded-full !border-0 !p-0 align-middle text-xs !shadow-none !shadow-transparent sm:me-2"
                >
                  {speculator?.speculator_profile_image_url ? (
                    <>
                      <img
                        className="inline-block rounded-full"
                        src={speculator.speculator_profile_image_url}
                        width="32"
                        height="32"
                        alt={`${speculator.speculator_username} profile image`}
                      />
                    </>
                  ) : (
                    <Icon name="user-circle" className="text-[#536485] dark:text-white" size={24} />
                  )}
                </button>
                <div className="dropdown-profile hidden cursor-pointer select-none md:block">
                  <p className="mb-0 text-[0.813rem] font-semibold leading-none text-[#536485]">
                    {speculator?.speculator_email}
                  </p>
                  <span className="block text-[0.6875rem] font-normal text-[#536485] opacity-[0.7]">
                    {speculator?.speculator_username}
                  </span>
                </div>
                <div
                  className="hs-dropdown-menu ti-dropdown-menu main-header-dropdown header-profile-dropdown dropdown-menu-end !-mt-3 hidden w-[11rem] select-none overflow-hidden border-0 border-defaultborder !p-0 pt-0"
                  aria-labelledby="dropdown-profile"
                >
                  <ul className="font-medium text-defaulttextcolor dark:text-[#8c9097] dark:text-white/50">
                    <li>
                      <Link
                        className="ti-dropdown-item !inline-flex w-full !gap-x-0 !p-[0.65rem] !text-[0.8125rem]"
                        to="/speculator/settings"
                      >
                        <i className="ti ti-adjustments-horizontal me-2 text-[1.125rem] opacity-[0.7]"></i>Settings
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="ti-dropdown-item !inline-flex w-full !gap-x-0 !p-[0.65rem] !text-[0.8125rem]"
                        to="/speculator/notifications"
                      >
                        <i className="ti ti-bell me-2 text-[1.125rem] opacity-[0.7]"></i>Notifications
                        {unreadCount > 0 && (
                          <span className="ms-auto rounded-full bg-primary px-2 py-0.5 text-[0.625rem] text-white">
                            {unreadCount}
                          </span>
                        )}
                      </Link>
                    </li>
                    <li className="flex flex-col">
                      <Link
                        className="ti-dropdown-item !inline-flex w-full !gap-x-0 !p-[0.65rem] !text-[0.8125rem]"
                        to="#"
                      >
                        <i className="ti ti-wallet me-2 text-[1.125rem] opacity-[0.7]"></i>
                        Bal: {usdFormatter.format(accountBalance)}
                      </Link>
                      <Link
                        className="ti-dropdown-item !inline-flex w-full !gap-x-0 !p-[0.65rem] !text-[0.8125rem]"
                        to="#"
                      >
                        <i className="ti ti-lock me-2 text-[1.125rem] opacity-[0.7]"></i>
                        Hold: {usdFormatter.format(heldBalance)}
                      </Link>
                    </li>

                    <li>
                      <button
                        onClick={generateReferralLink}
                        className="ti-dropdown-item !inline-flex w-full !gap-x-0 !p-[0.65rem] !text-[0.8125rem]"
                      >
                        <i className="ti ti-share me-2 text-[1.125rem] opacity-[0.7]"></i>Invite Friends
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={handleLogOut}
                        className="ti-dropdown-item !inline-flex w-full !gap-x-0 !p-[0.65rem] !text-[0.8125rem]"
                      >
                        <i className="ti ti-logout me-2 text-[1.125rem] opacity-[0.7]"></i>Log Out
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </nav>
      </header>
      {isReferralModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg dark:bg-gray-800">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Share Your Referral Link</h3>
              <button
                onClick={() => setIsReferralModalOpen(false)}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <i className="ti ti-x text-xl"></i>
              </button>
            </div>

            <div className="mb-4">
              <p className="mb-2 text-sm text-gray-600 dark:text-gray-400">
                Share this link with your friends and earn rewards when they sign up!
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={referralLink}
                  className="w-full rounded-md border border-gray-300 p-2 text-sm dark:border-gray-600 dark:bg-gray-700"
                />
                <button
                  onClick={copyToClipboard}
                  className="flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm text-white transition hover:bg-primary/90"
                >
                  {isCopied ? (
                    <>
                      <i className="ti ti-check text-lg"></i>
                      Copied!
                    </>
                  ) : (
                    <>
                      <i className="ti ti-copy text-lg"></i>
                      Copy
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setIsReferralModalOpen(false)}
                className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      <Modalsearch />
    </>
  );
}

export default Header;
